/**
 * geocodeCache.ts
 *
 * Caches and deduplicates geocode and reverse geocode API requests.
 * Prevents duplicate API calls when multiple components request the same location.
 */

import { Coordinates } from "./types";
import logger from "./utils/logger";

interface GeocodeCacheEntry {
  coordinates: Coordinates;
  formattedAddress?: string;
  timestamp: number;
}

interface ReverseGeocodeCacheEntry {
  formattedAddress: string;
  timestamp: number;
}

interface PendingGeocodeRequest {
  address: string;
  resolve: (value: GeocodeCacheEntry | null) => void;
  reject: (error: Error) => void;
  timestamp: number;
}

interface PendingReverseGeocodeRequest {
  coordinates: Coordinates;
  resolve: (value: string | null) => void;
  reject: (error: Error) => void;
  timestamp: number;
}

class GeocodeCache {
  // Cache for geocode results (address -> coordinates + formatted address)
  private geocodeCache = new Map<string, GeocodeCacheEntry>();

  // Cache for reverse geocode results (coordinates -> formatted address)
  private reverseGeocodeCache = new Map<string, ReverseGeocodeCacheEntry>();

  // Pending geocode requests (for deduplication)
  private pendingGeocodeRequests = new Map<string, PendingGeocodeRequest[]>();

  // Pending reverse geocode requests (for deduplication)
  private pendingReverseGeocodeRequests = new Map<
    string,
    PendingReverseGeocodeRequest[]
  >();

  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MAX_CACHE_SIZE = 1000;
  private isProcessingGeocode = false;
  private isProcessingReverseGeocode = false;
  private isInitialized = false;

  /**
   * Initialize cache from localStorage (persists across hot reloads)
   */
  private initializeCache(): void {
    if (this.isInitialized || typeof window === "undefined") {
      return;
    }

    try {
      // Load geocode cache from localStorage
      const geocodeCacheData = localStorage.getItem("geocodeCache");
      if (geocodeCacheData) {
        const parsed = JSON.parse(geocodeCacheData);
        const now = Date.now();
        Object.entries(parsed).forEach(([key, entry]: [string, unknown]) => {
          const cacheEntry = entry as GeocodeCacheEntry;
          if (now - cacheEntry.timestamp < this.CACHE_DURATION) {
            this.geocodeCache.set(key, cacheEntry);
          }
        });
      }

      // Load reverse geocode cache from localStorage
      const reverseGeocodeCacheData = localStorage.getItem(
        "reverseGeocodeCache",
      );
      if (reverseGeocodeCacheData) {
        const parsed = JSON.parse(reverseGeocodeCacheData);
        const now = Date.now();
        Object.entries(parsed).forEach(([key, entry]: [string, unknown]) => {
          const cacheEntry = entry as ReverseGeocodeCacheEntry;
          if (now - cacheEntry.timestamp < this.CACHE_DURATION) {
            this.reverseGeocodeCache.set(key, cacheEntry);
          }
        });
      }

      this.isInitialized = true;
    } catch (error) {
      console.warn(
        "[GEOCODE_CACHE] Failed to initialize from localStorage:",
        error,
      );
      this.isInitialized = true; // Mark as initialized to prevent retries
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveCacheToStorage(): void {
    if (typeof window === "undefined") {
      return;
    }

    try {
      // Save geocode cache
      const geocodeCacheObj: Record<string, GeocodeCacheEntry> = {};
      this.geocodeCache.forEach((value, key) => {
        geocodeCacheObj[key] = value;
      });
      localStorage.setItem("geocodeCache", JSON.stringify(geocodeCacheObj));

      // Save reverse geocode cache
      const reverseGeocodeCacheObj: Record<string, ReverseGeocodeCacheEntry> =
        {};
      this.reverseGeocodeCache.forEach((value, key) => {
        reverseGeocodeCacheObj[key] = value;
      });
      localStorage.setItem(
        "reverseGeocodeCache",
        JSON.stringify(reverseGeocodeCacheObj),
      );
    } catch (error) {
      console.warn("[GEOCODE_CACHE] Failed to save to localStorage:", error);
    }
  }

  /**
   * Generate cache key for coordinates
   */
  private coordinateKey(coords: Coordinates): string {
    // Round to 4 decimal places (~11 meters precision)
    const lat = Math.round(coords.lat * 10000) / 10000;
    const lng = Math.round(coords.lng * 10000) / 10000;
    return `${lat},${lng}`;
  }

  /**
   * Normalize address for cache key
   */
  private normalizeAddress(address: string): string {
    return address.trim().toLowerCase();
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();

    // Clean geocode cache
    for (const [key, entry] of this.geocodeCache.entries()) {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        this.geocodeCache.delete(key);
      }
    }

    // Clean reverse geocode cache
    for (const [key, entry] of this.reverseGeocodeCache.entries()) {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        this.reverseGeocodeCache.delete(key);
      }
    }

    // Limit cache size
    if (this.geocodeCache.size > this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.geocodeCache.entries()).sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      );
      const toRemove = entries.slice(
        0,
        entries.length - this.MAX_CACHE_SIZE + 100,
      );
      toRemove.forEach(([key]) => this.geocodeCache.delete(key));
    }

    if (this.reverseGeocodeCache.size > this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.reverseGeocodeCache.entries()).sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      );
      const toRemove = entries.slice(
        0,
        entries.length - this.MAX_CACHE_SIZE + 100,
      );
      toRemove.forEach(([key]) => this.reverseGeocodeCache.delete(key));
    }
  }

  /**
   * Get geocoded coordinates for an address (with caching and deduplication)
   */
  async geocode(address: string): Promise<GeocodeCacheEntry | null> {
    // Initialize cache from localStorage on first call
    if (!this.isInitialized) {
      this.initializeCache();
    }

    const normalizedAddress = this.normalizeAddress(address);

    // Check cache first
    const cached = this.geocodeCache.get(normalizedAddress);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      logger.log(`[GEOCODE_CACHE] Cache hit for: ${address}`);
      return cached;
    }

    logger.log(
      `[GEOCODE_CACHE] Cache miss for: ${address} (cache size: ${this.geocodeCache.size})`,
    );

    // Check if there's already a pending request for this address
    const pending = this.pendingGeocodeRequests.get(normalizedAddress);
    if (pending && pending.length > 0) {
      // Wait for existing request to complete
      return new Promise((resolve, reject) => {
        pending.push({
          address: normalizedAddress,
          resolve,
          reject,
          timestamp: Date.now(),
        });
      });
    }

    // Create new request
    return new Promise((resolve, reject) => {
      const request: PendingGeocodeRequest = {
        address: normalizedAddress,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.pendingGeocodeRequests.set(normalizedAddress, [request]);

      // Process the request
      this.processGeocodeRequest(normalizedAddress).catch((error) => {
        // Reject all pending requests for this address
        const requests = this.pendingGeocodeRequests.get(normalizedAddress);
        if (requests) {
          requests.forEach((req) => {
            req.reject(
              error instanceof Error ? error : new Error(String(error)),
            );
          });
          this.pendingGeocodeRequests.delete(normalizedAddress);
        }
      });
    });
  }

  /**
   * Process a geocode request
   */
  private async processGeocodeRequest(
    normalizedAddress: string,
  ): Promise<void> {
    if (this.isProcessingGeocode) {
      return;
    }

    this.isProcessingGeocode = true;

    const requests = this.pendingGeocodeRequests.get(normalizedAddress);
    if (!requests || requests.length === 0) {
      this.isProcessingGeocode = false;
      return;
    }

    try {
      // Make API call
      const response = await fetch("/api/maps/geocode", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address: requests[0].address, // Use original address from first request
        }),
      });

      if (!response.ok) {
        throw new Error(`Geocode API returned ${response.status}`);
      }

      const data = await response.json();

      // Handle both old format { lat, lng } and new format { coordinates: { lat, lng }, formattedAddress }
      let coordinates: Coordinates | null = null;
      let formattedAddress: string | undefined = undefined;

      if (data.coordinates) {
        // New format
        coordinates = data.coordinates;
        formattedAddress = data.formattedAddress;
      } else if (data.lat && data.lng) {
        // Old format (backward compatibility)
        coordinates = { lat: data.lat, lng: data.lng };
        formattedAddress = undefined;
      }

      if (coordinates) {
        const entry: GeocodeCacheEntry = {
          coordinates,
          formattedAddress,
          timestamp: Date.now(),
        };

        // Cache the result
        this.geocodeCache.set(normalizedAddress, entry);

        // Save to localStorage for persistence across hot reloads
        this.saveCacheToStorage();

        // Resolve all pending requests
        requests.forEach((req) => {
          req.resolve(entry);
        });
      } else {
        // No coordinates found
        requests.forEach((req) => {
          req.resolve(null);
        });
      }
    } catch (error) {
      // Reject all requests
      requests.forEach((req) => {
        req.reject(error instanceof Error ? error : new Error(String(error)));
      });
    } finally {
      this.pendingGeocodeRequests.delete(normalizedAddress);
      this.isProcessingGeocode = false;

      // Cleanup cache periodically
      if (Math.random() < 0.1) {
        this.cleanupCache();
      }
    }
  }

  /**
   * Get formatted address for coordinates (with caching and deduplication)
   */
  async reverseGeocode(coordinates: Coordinates): Promise<string | null> {
    const coordKey = this.coordinateKey(coordinates);

    // Check cache first
    const cached = this.reverseGeocodeCache.get(coordKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.formattedAddress;
    }

    // Check if there's already a pending request for these coordinates
    const pending = this.pendingReverseGeocodeRequests.get(coordKey);
    if (pending && pending.length > 0) {
      // Wait for existing request to complete
      return new Promise((resolve, reject) => {
        pending.push({
          coordinates,
          resolve,
          reject,
          timestamp: Date.now(),
        });
      });
    }

    // Create new request
    return new Promise((resolve, reject) => {
      const request: PendingReverseGeocodeRequest = {
        coordinates,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.pendingReverseGeocodeRequests.set(coordKey, [request]);

      // Process the request
      this.processReverseGeocodeRequest(coordKey, coordinates).catch(
        (error) => {
          // Reject all pending requests for these coordinates
          const requests = this.pendingReverseGeocodeRequests.get(coordKey);
          if (requests) {
            requests.forEach((req) => {
              req.reject(
                error instanceof Error ? error : new Error(String(error)),
              );
            });
            this.pendingReverseGeocodeRequests.delete(coordKey);
          }
        },
      );
    });
  }

  /**
   * Process a reverse geocode request
   */
  private async processReverseGeocodeRequest(
    coordKey: string,
    coordinates: Coordinates,
  ): Promise<void> {
    if (this.isProcessingReverseGeocode) {
      return;
    }

    this.isProcessingReverseGeocode = true;

    const requests = this.pendingReverseGeocodeRequests.get(coordKey);
    if (!requests || requests.length === 0) {
      this.isProcessingReverseGeocode = false;
      return;
    }

    try {
      // Make API call
      const response = await fetch("/api/maps/reverse-geocode", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lat: coordinates.lat,
          lng: coordinates.lng,
        }),
      });

      if (!response.ok) {
        throw new Error(`Reverse geocode API returned ${response.status}`);
      }

      const data = await response.json();

      if (data.formattedAddress) {
        const entry: ReverseGeocodeCacheEntry = {
          formattedAddress: data.formattedAddress,
          timestamp: Date.now(),
        };

        // Cache the result
        this.reverseGeocodeCache.set(coordKey, entry);

        // Save to localStorage for persistence across hot reloads
        this.saveCacheToStorage();

        // Resolve all pending requests
        requests.forEach((req) => {
          req.resolve(entry.formattedAddress);
        });
      } else {
        // No formatted address found
        requests.forEach((req) => {
          req.resolve(null);
        });
      }
    } catch (error) {
      // Reject all requests
      requests.forEach((req) => {
        req.reject(error instanceof Error ? error : new Error(String(error)));
      });
    } finally {
      this.pendingReverseGeocodeRequests.delete(coordKey);
      this.isProcessingReverseGeocode = false;

      // Cleanup cache periodically
      if (Math.random() < 0.1) {
        this.cleanupCache();
      }
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.geocodeCache.clear();
    this.reverseGeocodeCache.clear();
    this.pendingGeocodeRequests.clear();
    this.pendingReverseGeocodeRequests.clear();
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats() {
    return {
      geocodeCacheSize: this.geocodeCache.size,
      reverseGeocodeCacheSize: this.reverseGeocodeCache.size,
      pendingGeocodeRequests: this.pendingGeocodeRequests.size,
      pendingReverseGeocodeRequests: this.pendingReverseGeocodeRequests.size,
    };
  }
}

// Export singleton instance
export const geocodeCache = new GeocodeCache();
